import type { InputNumberProps } from 'antd';
import { InputNumber, Select } from 'antd';
import {
  hoursDaysDisplayFormatter,
  hoursDaysDisplayParser,
  hoursToDecimalDaysDisplayFormatter,
  hoursToDecimalDaysDisplayParser,
} from '../fulfillmentContext/utils';
import type { DurationTypes, ValueType } from '../types';

export interface DurationInputNumberProps extends InputNumberProps {
  timeMode: DurationTypes;
  setTimeMode: React.Dispatch<React.SetStateAction<DurationTypes>>;
  // The shared parsers coerce an empty string to '0'. Callers that must tell "no value" apart from a
  // deliberate 0 pass allowEmpty so a cleared field stays undefined instead of silently becoming zero.
  allowEmpty?: boolean;
}

const emptyPreservingParser =
  (parser: (val: string | undefined) => string) =>
  (val: string | undefined): string => {
    if (val === undefined || val.trim() === '') {
      return '';
    }
    return parser(val);
  };

// The shared formatters render undefined as "0 Days 0 Hours". Where 0 is a meaningful value that has to be
// distinguishable from unset, an empty field must look empty.
const emptyPreservingFormatter =
  (formatter: (val: ValueType, info: { userTyping: boolean; input: string }) => string) =>
  (val: ValueType, info: { userTyping: boolean; input: string }): string => {
    if (val === undefined || val === null || val === '') {
      return '';
    }
    return formatter(val, info);
  };

const DAYS = 14;
const HOURS = DAYS * 24;

// wraps a format function, and returns a function correctly typed for Antd's formatter prop
const antFormatterWrapper: (val: any) => (val: ValueType, info: { userTyping: boolean; input: string }) => string = (
  fnToWrap: any,
) => {
  return (val: ValueType, info: { userTyping: boolean; input: string }) => fnToWrap(val);
};

// Both functions are required so callers never have to test for their presence — every DurationTypes
// entry below supplies both, and timeMode is a required prop of that union.
const inputFunctionLookup: Record<
  DurationTypes,
  {
    formatter: (displayValue: ValueType, info: { userTyping: boolean; input: string }) => string;
    parser: (displayValue: string | undefined) => string;
  }
> = {
  Time: {
    formatter: antFormatterWrapper(hoursDaysDisplayFormatter),
    parser: hoursDaysDisplayParser,
  },
  Days: {
    formatter: antFormatterWrapper(hoursToDecimalDaysDisplayFormatter),
    parser: hoursToDecimalDaysDisplayParser,
  },
  Hours: {
    formatter: (val: any) => val,
    parser: (val: any) => val,
  },
};

const DurationInputNumber = ({ timeMode, setTimeMode, allowEmpty, ...inputNumberProps }: DurationInputNumberProps) => {
  const selectAfter = (
    <Select
      defaultValue="Time"
      value={timeMode}
      style={{
        width: 100,
        textAlign: 'left',
      }}
      onChange={(val) => {
        setTimeMode(val as DurationTypes);
      }}
    >
      <Select.Option value="Time">Time</Select.Option>
      <Select.Option value="Hours">Hours</Select.Option>
      <Select.Option value="Days">Days</Select.Option>
    </Select>
  );

  const inputProps: InputNumberProps = {
    style: {
      width: '360px',
    },
    min: 0,
    max: HOURS,
    step: 1,
    addonAfter: selectAfter,
    ...inputNumberProps,
  };

  const modeFunctions = inputFunctionLookup[timeMode];
  const functions = allowEmpty
    ? {
        parser: emptyPreservingParser(modeFunctions.parser),
        formatter: emptyPreservingFormatter(modeFunctions.formatter),
      }
    : modeFunctions;

  return <InputNumber key={timeMode} {...functions} {...inputProps} />;
};

export default DurationInputNumber;
